import React, { useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { PaymentFailProps, PaymentFailErrorInfo } from './PaymentFail.types';
import { adaptPaymentFailProps } from './PaymentFail.adapter';
import { useTranslation } from '../../../redux/TranslationHelper';
import '../PaymentSuccess/PaymentSuccess.css'; // 같은 스타일 사용

/**
 * 결제 실패 컴포넌트
 */
const PaymentFailComponent: React.FC<any> = (originalProps) => {
  // 어댑터를 통해 props 변환
  const props = useMemo(() => adaptPaymentFailProps(originalProps), [originalProps]);
  
  // t 함수를 메모이제이션하여 무한 렌더링 방지
  const { t } = useTranslation();
  
  // 컴포넌트 속성 추출
  const {
    style = {},
    className = '',
    componentProps,
    isEditor = false
  } = props;

  // 컴포넌트 속성에서 필요한 값들 추출
  const {
    title = t("결제에 실패했습니다"),
    errorDefaultMessage = t("결제 처리 중 오류가 발생했습니다."),
    cartButtonText = t("장바구니로 돌아가기"),
    homeButtonText = t("홈으로 돌아가기"),
    showErrorDetails = true,
    redirectToHome = "/",
    redirectToCart = "/cart"
  } = componentProps || {};

  // 기본 스타일 + props로 받은 스타일 병합
  const mergedStyle = useMemo(() => ({
    width: '100%',
    ...style
  }), [style]);
  
  const location = useLocation();
  const navigate = useNavigate();
  
  // URL에서 에러 정보 추출
  const errorInfo = useMemo<PaymentFailErrorInfo>(() => {
    if (isEditor) {
      // 에디터 모드에서는 미리보기 정보 제공
      return {
        errorCode: 'EDITOR_MODE',
        errorMessage: t('에디터 모드: 실제 오류 정보는 실행 시 나타납니다.'),
        orderId: 'ORDER_SAMPLE'
      };
    }
    
    const searchParams = new URLSearchParams(location.search);
    return {
      errorCode: searchParams.get('code'),
      errorMessage: searchParams.get('message') || errorDefaultMessage,
      orderId: searchParams.get('orderId')
    };
  }, [location.search, errorDefaultMessage, isEditor, t]);

  // 이벤트 핸들러 메모이제이션
  const handleGoToCart = useCallback(() => {
    navigate(redirectToCart);
  }, [navigate, redirectToCart]);

  const handleGoToHome = useCallback(() => {
    navigate(redirectToHome);
  }, [navigate, redirectToHome]);

  return (
    <div className={className} style={mergedStyle}>
      <div className="payment-result-container">
        <div className="payment-result-card error">
          <div className="error-icon">❌</div>
          <h2>{title}</h2>
          
          {showErrorDetails && (
            <div className="payment-info">
              {errorInfo.orderId && (
                <div className="info-row">
                  <span className="label">{t("주문번호")}:</span>
                  <span className="value">{errorInfo.orderId}</span>
                </div>
              )}
              {errorInfo.errorCode && (
                <div className="info-row">
                  <span className="label">{t("오류 코드")}:</span>
                  <span className="value">{errorInfo.errorCode}</span>
                </div>
              )}
              <div className="info-row">
                <span className="label">{t("오류 메시지")}:</span>
                <span className="value">{errorInfo.errorMessage}</span>
              </div>
            </div>
          )}
          
          <div className="payment-action-buttons">
            <button onClick={handleGoToCart} className="view-cart-button">
              {cartButtonText}
            </button>
            <button onClick={handleGoToHome} className="go-home-button">
              {homeButtonText}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentFailComponent; 